import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { Row, Col, Button, Modal, Collapse, Checkbox, Divider, Spin, message } from 'antd';
import { EyeOutlined, CloseOutlined } from '@ant-design/icons';
import s from './s.module.less';
import pageS from '../../s.module.less';
import { DefaultPaymentPolicies, DefaultTreatmentConsent, DefaultHipaaConsent } from './polices';
import CollapsibleConsentCard from './CollapsibleConsentCard';
import { useConsentForm } from 'hooks/paymentSetup/useConsentForm';
import { editConsentForm, editConsentFormByOps } from 'api/paymentSetup';
import { ConsentForm, ConsentFormType } from 'types/paymentSetup';

const { Panel } = Collapse;

interface ConsentFormsStepProps {
    isEdit?: boolean;
    isReview?: boolean;
    isOps?: boolean;
    providerId?: number;
}
export interface ConsentFormsStepHandles {
    onSubmit: () => Promise<void>;
}

const ConsentFormsStep = forwardRef<ConsentFormsStepHandles, ConsentFormsStepProps>(({
    isEdit = true,
    isReview = false,
    isOps = false,
    providerId,
}, ref) => {
    const [paymentPolicyContent, setPaymentPolicyContent] = useState<string>(DefaultPaymentPolicies);
    const [generalConsentContent, setGeneralConsentContent] = useState<string>(DefaultTreatmentConsent);
    const [hipaaContent, setHipaaContent] = useState<string>(DefaultHipaaConsent);
    const [activeKeys, setActiveKeys] = useState<string[]>([]);
    const [previewVisible, setPreviewVisible] = useState<boolean>(false);
    const [previewActiveKeys, setPreviewActiveKeys] = useState<string[]>([]);
    const paymentPolicyCardRef = useRef<any>(null);
    const generalConsentCardRef = useRef<any>(null);
    const hipaaCardRef = useRef<any>(null);

    const [apiFormData, loading, fetchData] = useConsentForm(isOps, providerId);

    useEffect(() => {
        if (Array.isArray(apiFormData) && apiFormData.length > 0) {
            apiFormData.forEach((item: ConsentForm) => {
                if (item.type === ConsentFormType.PAYMENT_CANCELLATION) {
                    setPaymentPolicyContent(item.content);
                }
                if (item.type === ConsentFormType.TREATMENT_CONSENT) {
                    setGeneralConsentContent(item.content);
                }
                if (item.type === ConsentFormType.HIPAA_PRIVACY) {
                    setHipaaContent(item.content);
                }
            });
        }
    }, [apiFormData]);

    const handleStepSubmit = async () => {
        try {
            const isPaymentPolicyEditing = paymentPolicyCardRef.current?.isEditing;
            const isGeneralConsentEditing = generalConsentCardRef.current?.isEditing;
            const isHipaaEditing = hipaaCardRef.current?.isEditing;

            if (isPaymentPolicyEditing || isGeneralConsentEditing || isHipaaEditing) {
                message.error("You must click 'Save changes' at the bottom of each form to keep your edits.");
                throw new Error('error');
            }

            await Promise.all([
                paymentPolicyCardRef.current?.onValidate(),
                generalConsentCardRef.current?.onValidate(),
                hipaaCardRef.current?.onValidate(),
            ]);
            const payload: ConsentForm[] = [
                { type: ConsentFormType.PAYMENT_CANCELLATION, content: paymentPolicyContent },
                { type: ConsentFormType.TREATMENT_CONSENT, content: generalConsentContent },
                { type: ConsentFormType.HIPAA_PRIVACY, content: hipaaContent },
            ];
            const api = isOps ? editConsentFormByOps(payload, providerId!) : editConsentForm(payload);
            await api;
            await fetchData();
        } catch (errorInfo) {
            console.log('Save failed:', errorInfo);
            throw errorInfo;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit: handleStepSubmit,
    }));

    const handleFormValueChange = (fieldName: string, value: string) => {
        switch (fieldName) {
            case 'paymentPolicyContent':
                setPaymentPolicyContent(value);
                break;
            case 'generalConsentContent':
                setGeneralConsentContent(value);
                break;
            case 'hipaaContent':
                setHipaaContent(value);
                break;
            default:
                break;
        }
    };

    const showPreviewModal = () => {
        setPreviewVisible(true);
    };

    const closePreviewModal = () => {
        setPreviewVisible(false);
        setPreviewActiveKeys([]);
    };

    const handlePreviewPanelChange = (keys: string | string[]) => {
        setPreviewActiveKeys(typeof keys === 'string' ? [keys] : keys);
    };

    if (!isEdit) {
        return (
            <div className={s.summaryContent}>
                <Row>
                    <Col span={24}>
                        <div className={s.summaryItem}>
                            <div className={s.summaryLabel}>Payment & Cancellation Policy</div>
                            <div className={s.summaryValue} dangerouslySetInnerHTML={{ __html: paymentPolicyContent }} />
                        </div>
                    </Col>

                    <Divider />

                    <Col span={24}>
                        <div className={s.summaryItem}>
                            <div className={s.summaryLabel}>General Consent for Treatment</div>
                            <div className={s.summaryValue} dangerouslySetInnerHTML={{ __html: generalConsentContent }} />
                        </div>
                    </Col>

                    <Divider />

                    <Col span={24}>
                        <div className={s.summaryItem}>
                            <div className={s.summaryLabel}>HIPAA Notice of Privacy Practices & Acknowledgment</div>
                            <div className={s.summaryValue} dangerouslySetInnerHTML={{ __html: hipaaContent }} />
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }

    return (

        <div className={s.consentFormsStep}>
            {!isReview && (
                <div className={s.header}>
                    <h2 className={pageS.stepTitle}>Consent forms</h2>
                    <p className={pageS.desc}>Customize what patients need to review and sign before their appointments.</p>
                </div>
            )}

            <div className={s.formSection}>
                {!isReview && (
                    <>
                        <h3 className={pageS.title}>Review and edit agreements</h3>
                        <p className={pageS.desc}>Use our default templates or update them to match your practice needs.</p>
                    </>
                )}

                <Spin spinning={loading}>
                    <div className={s.cardContainer}>
                        <CollapsibleConsentCard
                            title="Payment & Cancellation Policy"
                            description="Helps prevent disputes by clearly outlining payment terms, no-show fees, and cancellation rules."
                            content={paymentPolicyContent}
                            defaultContent={DefaultPaymentPolicies}
                            fieldName="paymentPolicyContent"
                            panelKey="1"
                            activeKeys={activeKeys}
                            onActiveKeysChange={setActiveKeys}
                            onChange={handleFormValueChange}
                            ref={paymentPolicyCardRef}
                        />

                        <CollapsibleConsentCard
                            title="General Consent for Treatment"
                            description="Allows you to legally evaluate and treat patients, forming the foundation of all care provided."
                            content={generalConsentContent}
                            defaultContent={DefaultTreatmentConsent}
                            fieldName="generalConsentContent"
                            panelKey="2"
                            activeKeys={activeKeys}
                            onActiveKeysChange={setActiveKeys}
                            onChange={handleFormValueChange}
                            ref={generalConsentCardRef}
                        />

                        <CollapsibleConsentCard
                            title="HIPAA Notice of Privacy Practices & Acknowledgment"
                            description="Keeps your practice compliant and informs patients of their privacy rights."
                            content={hipaaContent}
                            defaultContent={DefaultHipaaConsent}
                            fieldName="hipaaContent"
                            panelKey="3"
                            activeKeys={activeKeys}
                            onActiveKeysChange={setActiveKeys}
                            onChange={handleFormValueChange}
                            ref={hipaaCardRef}
                        />
                    </div>
                </Spin>
            </div>

            <div className={s.previewButtonContainer}>
                <Button className={s.previewButton} icon={<EyeOutlined />} onClick={showPreviewModal}>
                    Preview as patient
                </Button>
            </div>

            <Modal
                title="Patient view"
                open={previewVisible}
                footer={null}
                onCancel={closePreviewModal}
                width={872}
                maskClosable={false}
                closeIcon={<CloseOutlined />}
                className={s.patientPreviewModal}
            >
                <div className={s.patientPreviewContent}>
                    <Collapse
                        activeKey={previewActiveKeys}
                        onChange={handlePreviewPanelChange}
                        ghost
                        className={s.previewCollapse}
                    >
                        <Panel
                            header="Payment & Cancellation Policy"
                            key="1"
                            className={s.previewPanel}
                        >
                            <div className={s.previewContent} dangerouslySetInnerHTML={{ __html: paymentPolicyContent }} />
                        </Panel>
                        <Panel
                            header="General Consent for Treatment"
                            key="2"
                            className={s.previewPanel}
                        >
                            <div className={s.previewContent} dangerouslySetInnerHTML={{ __html: generalConsentContent }} />
                        </Panel>
                        <Panel
                            header="HIPAA Notice of Privacy Practices & Acknowledgment"
                            key="3"
                            className={s.previewPanel}
                        >
                            <div className={s.previewContent} dangerouslySetInnerHTML={{ __html: hipaaContent }} />
                        </Panel>
                    </Collapse>

                    <Divider />
                    <div className={s.previewAcknowledgement}>
                        <Checkbox>By checking the box below, I acknowledge that I have read, understood, and agree to these terms.</Checkbox>
                    </div>
                </div>
            </Modal>
        </div>
    );
});

export default ConsentFormsStep;
